/**
 * renderStatementHtml — HTML-Render der Stellungnahme aus dem flachen
 * StatementPlaceholders-Map (gleiche Quelle wie der DOCX-Renderer).
 *
 * Zweck: PDF-Export ohne Server-Roundtrip. Der Browser hat einen ausgereiften
 * PDF-Renderer eingebaut (`window.print` → „Als PDF speichern"). Damit
 * bleibt die DSGVO-Garantie „Daten verlassen nicht den Endgerät" erhalten —
 * wir brauchen keinen LibreOffice-Worker, keine Cloud-Conversion.
 *
 * Das Layout folgt der Struktur des DOCX-Templates (Briefkopf, Empfänger,
 * Score-Block, Kennzahlen-Tabelle, Verdict, Dimensionen, Risiken, Unterschrift),
 * ist aber bewusst eigenständig gehalten — nicht via mammoth.js aus dem DOCX
 * extrahiert. Vorteile dieser Trennung:
 *   - Keine zusätzliche Library-Abhängigkeit (mammoth ~ 600 KB).
 *   - Volle Kontrolle über Print-CSS (A4, Margins, Page-Breaks).
 *   - HTML-Output kann später auch für Mandantenpfade (B2C-Vorschau im Wizard)
 *     wiederverwendet werden.
 *
 * Pure Function — gibt einen vollständigen HTML-Dokument-String zurück.
 * Aufrufer schreibt den String in ein neues window via document.write oder
 * blob:-URL.
 *
 * @see ./renderDocx.ts (gleiche Eingabe, anderes Output-Format)
 * @see ./printPdf.ts   (öffnet das HTML im Browser-Print-Dialog)
 */

import type { statement } from '@tragfaehigkeit/core';

type StatementPlaceholders = statement.StatementPlaceholders;

/** HTML-escape: verhindert dass Mandanten-Texte HTML/Skript einschleusen können. */
function esc(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Mehrzeiliger Text → <p>-Block, Zeilenumbrüche bleiben erhalten. */
function paragraphs(input: string): string {
  if (input.length === 0) return '';
  return input
    .split(/\n{2,}/)
    .map((para) => `<p>${esc(para).replace(/\n/g, '<br />')}</p>`)
    .join('');
}

/** Aufzählung: Zeilen mit "·" oder "-" Prefix → <ul>; sonst Absätze. */
function listOrParagraphs(input: string): string {
  if (input.length === 0) return '';
  const lines = input.split(/\n+/).filter((l) => l.trim().length > 0);
  const looksLikeList = lines.length >= 2 && lines.every((l) => /^[-·•*]\s+/.test(l.trim()));
  if (looksLikeList) {
    const items = lines.map((l) => `<li>${esc(l.trim().replace(/^[-·•*]\s+/, ''))}</li>`).join('');
    return `<ul>${items}</ul>`;
  }
  return paragraphs(input);
}

const NOT_RECORDED_HTML = '<span class="not-recorded">nicht erfasst</span>';

/** Optionaler Berater-Kommentar unter einer Kennzahl. Leer → kein leeres
 *  Element (vermeidet leere Zeilen im Gutachten). */
function miniNote(text: string): string {
  return text.trim().length > 0 ? `<br /><span class="label-mini">${esc(text)}</span>` : '';
}

/** Beratungsgespräch-Zeile: Datum + optional Format/Dauer, sonst „nicht erfasst". */
function renderMeetingLine(get: (k: string) => string): string {
  const date = get('MEETING_DATE').trim();
  if (date.length === 0) return NOT_RECORDED_HTML;
  const format = get('MEETING_FORMAT').trim();
  const duration = get('MEETING_DURATION_MIN').trim();
  const suffixParts: string[] = [];
  if (format.length > 0) suffixParts.push(esc(format));
  if (duration.length > 0) suffixParts.push(`${esc(duration)} Min.`);
  const suffix = suffixParts.length > 0 ? ` (${suffixParts.join(', ')})` : '';
  return `${esc(date)}${suffix}`;
}

/** „Geprüfte Unterlagen": echte Datei-Listen; fehlt eine Kategorie, wird das
 *  ehrlich als „nicht vorgelegt" ausgewiesen statt einer erfundenen Angabe. */
function renderDocumentList(get: (k: string) => string): string {
  const bwa = get('DOCUMENT_LIST_BWA').trim();
  const eks = get('DOCUMENT_LIST_EKS').trim();
  const additional = get('DOCUMENT_LIST_ADDITIONAL').trim();
  const lines: string[] = [
    bwa.length > 0 ? `BWA: ${esc(bwa)}` : '<span class="not-recorded">Keine BWA vorgelegt</span>',
    eks.length > 0
      ? `Anlage EKS: ${esc(eks)}`
      : '<span class="not-recorded">Keine Anlage EKS vorgelegt</span>',
  ];
  if (additional.length > 0) lines.push(esc(additional));
  return lines.join('<br />');
}

/** Ampel-Farbe als CSS-Color (für DIM-Score-Badges). */
function trafficColor(label: string): string {
  if (label === 'grün') return 'var(--color-traffic-green)';
  if (label === 'gelb') return 'var(--color-traffic-yellow)';
  if (label === 'rot') return 'var(--color-traffic-red)';
  return 'var(--color-stone-400)';
}

const DIM_LABELS: ReadonlyArray<{ readonly num: number; readonly title: string }> = [
  { num: 1, title: 'Identität & Qualifikation' },
  { num: 2, title: 'Aktivität & Geschäftsmodell' },
  { num: 3, title: 'Stil & Marktposition' },
  { num: 4, title: 'Ressourcen & Finanzen' },
  { num: 5, title: 'Gesundheit & Resilienz' },
  { num: 6, title: 'Privates Umfeld & Bedarfsgemeinschaft' },
];

/**
 * Erzeugt das vollständige HTML-Dokument für den Browser-Druck.
 *
 * Print-Strategie:
 *   - A4 mit 22 mm/24 mm Rändern (PDF-Standard für Behördenpost).
 *   - Source Serif Variable wird via @font-face referenziert (im _headers
 *     erlaubt, gleiche Quelle wie die App).
 *   - `print-color-adjust: exact` damit der Petschwork-Logo-Akzent + die
 *     Score-Box auch im Druck farbig bleiben.
 *   - `page-break-inside: avoid` für Score-Block, Dimensionen, Verdict —
 *     nichts darf mitten durch eine Tabellenzeile brechen.
 */
export function renderStatementHtml(p: StatementPlaceholders): string {
  const get = (key: string): string => p[key] ?? '';
  const NICHT_ERFASST = 'nicht erfasst';
  // Für Pflicht-Zeilen, die immer im Dokument stehen: leerer Wert wird ehrlich
  // als „nicht erfasst" ausgewiesen statt als leere Zelle oder erfundener Wert.
  const orNr = (key: string): string => {
    const v = get(key);
    return v.trim().length > 0 ? esc(v) : `<span class="not-recorded">${NICHT_ERFASST}</span>`;
  };

  const dimensions = DIM_LABELS.map(({ num, title }) => {
    const score = get(`DIM${String(num)}_SCORE`);
    const traffic = get(`DIM${String(num)}_TRAFFIC`);
    const short = get(`DIM${String(num)}_SHORT`);
    const detail = get(`DIM${String(num)}_DETAIL`);
    return `
      <section class="dim">
        <header class="dim-head">
          <span class="dim-num">${String(num)}</span>
          <div>
            <h3>${esc(title)}</h3>
            <p class="dim-short">${esc(short)}</p>
          </div>
          <span class="dim-badge" style="background:${trafficColor(traffic)}">
            <span class="dim-score">${esc(score)}</span>
            <span class="dim-traffic">${esc(traffic)}</span>
          </span>
        </header>
        ${paragraphs(detail)}
      </section>
    `;
  }).join('\n');

  const conditions = get('CONDITIONS_PARAGRAPH').trim();
  const reevaluation = get('REEVALUATION_PROPOSAL').trim();

  // Dokument-Titel (für Browser-Tab + Druck-Fußzeile)
  const docTitle = `Stellungnahme ${get('CASE_ID')} – ${get('CLIENT_FULL_NAME')}`;

  return `<!doctype html>
<html lang="de">
<head>
  <meta charset="utf-8" />
  <title>${esc(docTitle)}</title>
  <style>
    /* ---- Brand-Token (Inline, damit der neue Window-Context unabhängig ist) ---- */
    :root {
      --color-stone-900: #1c1917;
      --color-stone-700: #44403c;
      --color-stone-500: #78716c;
      --color-stone-400: #a8a29e;
      --color-stone-200: #e7e5e4;
      --color-stone-100: #f5f5f4;
      --color-stone-50:  #fafaf9;
      /* Abgedunkelt für ausreichenden Kontrast von weißem Badge-Text (≥ 4.5:1,
         WCAG 1.4.3) — die hellen Ampeltöne (#3aaa6d/#c5a14b) reichten nur ~2.3–2.9:1. */
      --color-traffic-green:  #2e7d51;
      --color-traffic-yellow: #8a6a1e;
      --color-traffic-red:    #a3372f;
      --color-petschwork-accent: #b8a07a;
    }
    @font-face {
      font-family: "Source Serif 4";
      src: url("/fonts/SourceSerif4-Variable.woff2") format("woff2-variations");
      font-weight: 200 900;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: "Inter";
      src: url("/fonts/Inter-Variable.woff2") format("woff2-variations");
      font-weight: 100 900;
      font-style: normal;
      font-display: swap;
    }
    /* ---- A4-Druck-Setup ---- */
    @page {
      size: A4;
      margin: 22mm 24mm 22mm 24mm;
    }
    @media print {
      .no-print { display: none !important; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .dim, .verdict-block, .score-block, .signature { page-break-inside: avoid; }
      .section-break { page-break-before: always; }
    }
    /* ---- Basis ---- */
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      font-family: "Source Serif 4", "Source Serif Pro", Georgia, serif;
      color: var(--color-stone-900);
      font-size: 10.5pt;
      line-height: 1.45;
      background: #fff;
    }
    body {
      max-width: 210mm;
      margin: 0 auto;
      padding: 18mm 22mm 30mm 22mm;
    }
    @media print {
      body { padding: 0; max-width: none; }
    }
    h1, h2, h3, h4 {
      font-family: "Source Serif 4", Georgia, serif;
      color: var(--color-stone-900);
      margin: 0 0 0.4em 0;
      font-weight: 700;
    }
    h1 { font-size: 16pt; line-height: 1.2; }
    h2 { font-size: 13pt; line-height: 1.25; margin-top: 1.6em; border-bottom: 1px solid var(--color-stone-200); padding-bottom: 0.3em; }
    h3 { font-size: 11pt; line-height: 1.3; }
    p { margin: 0 0 0.6em 0; }
    ul { margin: 0.2em 0 0.8em 1.2em; padding: 0; }
    li { margin-bottom: 0.25em; }
    table { border-collapse: collapse; width: 100%; margin: 0.6em 0; }
    th, td { padding: 5px 8px; text-align: left; vertical-align: top; }
    th {
      font-family: "Inter", -apple-system, sans-serif;
      font-size: 8.5pt;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--color-stone-500);
      border-bottom: 1px solid var(--color-stone-200);
    }
    td { border-bottom: 1px solid var(--color-stone-100); }
    .label-mini {
      font-family: "Inter", -apple-system, sans-serif;
      font-size: 8pt;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: var(--color-stone-500);
    }
    .not-recorded {
      font-style: italic;
      color: var(--color-stone-400);
    }

    /* ---- Print-Toolbar (nur Bildschirm) ---- */
    .print-toolbar {
      position: sticky;
      top: 0;
      z-index: 10;
      background: var(--color-stone-900);
      color: #fff;
      padding: 12px 22mm;
      margin: -18mm -22mm 24mm -22mm;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
    }
    .print-toolbar p {
      margin: 0;
      font-family: "Inter", sans-serif;
      font-size: 10pt;
    }
    .print-toolbar button {
      background: #fff;
      color: var(--color-stone-900);
      border: 0;
      border-radius: 2px;
      padding: 8px 18px;
      font-family: "Inter", sans-serif;
      font-size: 9pt;
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      cursor: pointer;
    }
    .print-toolbar button:hover { background: var(--color-stone-100); }

    /* ---- Briefkopf ---- */
    .header {
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: end;
      gap: 24px;
      padding-bottom: 16px;
      border-bottom: 2px solid var(--color-petschwork-accent);
    }
    .header-meta {
      font-family: "Inter", sans-serif;
      font-size: 8.5pt;
      color: var(--color-stone-500);
      line-height: 1.5;
    }
    .header-meta strong { color: var(--color-stone-900); font-weight: 600; }
    .header-logo img {
      max-height: 56px;
      width: auto;
    }

    /* ---- Empfänger ---- */
    .recipient-row {
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 24px;
      margin-top: 18mm;
    }
    .recipient address {
      font-style: normal;
      line-height: 1.5;
    }
    .recipient .small { font-size: 9pt; color: var(--color-stone-500); }
    .statement-date {
      text-align: right;
      font-family: "Inter", sans-serif;
      font-size: 9.5pt;
      color: var(--color-stone-700);
    }

    /* ---- Subject ---- */
    .subject {
      margin-top: 14mm;
      font-weight: 700;
      font-size: 12pt;
    }
    .subject .file-no {
      display: block;
      font-weight: 400;
      font-size: 10pt;
      color: var(--color-stone-500);
      margin-top: 2px;
    }

    /* ---- Score-Block ---- */
    .score-block {
      margin-top: 16px;
      border: 1px solid var(--color-stone-200);
      border-left: 4px solid var(--color-petschwork-accent);
      padding: 14px 18px;
      background: var(--color-stone-50);
      display: grid;
      grid-template-columns: auto auto 1fr;
      gap: 24px;
      align-items: center;
    }
    .score-num {
      font-size: 36pt;
      font-weight: 800;
      line-height: 1;
      color: var(--color-stone-900);
    }
    .score-num small {
      font-size: 12pt;
      font-weight: 400;
      color: var(--color-stone-500);
      margin-left: 4px;
    }
    .verdict-pill {
      font-family: "Inter", sans-serif;
      font-size: 10pt;
      padding: 6px 10px;
      background: var(--color-stone-900);
      color: #fff;
      border-radius: 2px;
    }
    .confidence {
      font-size: 9.5pt;
      color: var(--color-stone-700);
      line-height: 1.4;
    }
    .confidence strong { color: var(--color-stone-900); }
    .confidence .conf-explain {
      display: block;
      font-size: 8.5pt;
      font-style: italic;
      color: var(--color-stone-500);
      margin-top: 2px;
    }

    /* ---- Kennzahlen-Tabellen ---- */
    .kpi-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-top: 12px; }

    /* ---- Dimensionen ---- */
    .dim {
      margin: 14px 0;
      padding: 12px 14px;
      border: 1px solid var(--color-stone-200);
      border-radius: 2px;
    }
    .dim-head {
      display: grid;
      grid-template-columns: auto 1fr auto;
      gap: 14px;
      align-items: center;
      margin-bottom: 8px;
    }
    .dim-num {
      font-family: "Source Serif 4", serif;
      font-size: 24pt;
      font-weight: 800;
      color: var(--color-stone-400);
      line-height: 1;
    }
    .dim-head h3 { margin: 0 0 2px 0; font-size: 11pt; }
    .dim-short {
      margin: 0;
      font-size: 9pt;
      font-style: italic;
      color: var(--color-stone-500);
    }
    .dim-badge {
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      padding: 6px 10px;
      border-radius: 2px;
      color: #fff;
      min-width: 56px;
    }
    .dim-score {
      font-family: "Inter", sans-serif;
      font-size: 13pt;
      font-weight: 700;
      line-height: 1.1;
    }
    .dim-traffic {
      font-family: "Inter", sans-serif;
      font-size: 7.5pt;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      opacity: 0.9;
    }

    /* ---- Verdict-Block ---- */
    .verdict-block {
      margin-top: 18px;
      padding: 14px 18px;
      background: var(--color-stone-100);
      border-radius: 2px;
    }
    .verdict-block p:last-child { margin-bottom: 0; }

    /* ---- Unterschrift ---- */
    .signature {
      margin-top: 28mm;
      padding-top: 12px;
      border-top: 1px solid var(--color-stone-200);
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 24px;
    }
    .signature .label-mini { display: block; margin-bottom: 6px; }
    .signature-name {
      font-weight: 600;
      font-size: 11pt;
    }
    .footer {
      margin-top: 18mm;
      padding-top: 8px;
      border-top: 1px dashed var(--color-stone-200);
      font-family: "Inter", sans-serif;
      font-size: 8pt;
      color: var(--color-stone-400);
      text-align: center;
    }
  </style>
</head>
<body>
  <div class="print-toolbar no-print">
    <p>Stellungnahme-Vorschau · <strong>${esc(get('CASE_ID'))}</strong> · ${esc(get('CLIENT_FULL_NAME'))}</p>
    <button type="button" onclick="window.print()">Als PDF drucken / speichern →</button>
  </div>

  <header class="header">
    <div class="header-meta">
      <strong>${esc(get('PROVIDER_NAME'))}</strong><br />
      ${esc(get('PROVIDER_ADDRESS_LINE1'))}<br />
      ${esc(get('PROVIDER_ADDRESS_LINE2'))}<br />
      ${esc(get('PROVIDER_PHONE_EMAIL'))}<br />
      <span style="color: var(--color-stone-400); font-size: 8pt;">${esc(get('PROVIDER_CERTIFICATIONS'))}</span>
    </div>
    <div class="header-logo">
      <img src="/templates/petschwork-logo.png" alt="Petschwork Consulting" />
    </div>
  </header>

  <section class="recipient-row">
    <div class="recipient">
      <p class="label-mini">An</p>
      <address>
        Jobcenter ${esc(get('JC_NAME'))}<br />
        ${get('JC_CONTACT_NAME') ? `<span>${esc(get('JC_CONTACT_NAME'))}</span><br />` : ''}
        ${esc(get('JC_ADDRESS_LINE1'))}<br />
        ${esc(get('JC_ADDRESS_LINE2'))}
      </address>
    </div>
    <div class="statement-date">
      ${esc(get('STATEMENT_DATE'))}
    </div>
  </section>

  <h1 class="subject">
    Stellungnahme zur Tragfähigkeit der selbständigen Tätigkeit
    ${get('JC_FILE_NUMBER') ? `<span class="file-no">Aktenzeichen ${esc(get('JC_FILE_NUMBER'))}</span>` : ''}
  </h1>

  <p>${esc(get('JC_SALUTATION') || 'Sehr geehrte Damen und Herren')},</p>
  <p>auf Grundlage Ihres Auftrags vom <strong>${esc(get('ORDER_DATE'))}</strong> haben wir die wirtschaftliche und persönliche Tragfähigkeit der selbständigen Tätigkeit von <strong>${esc(get('CLIENT_FULL_NAME'))}</strong> nach den methodischen Vorgaben der Fachlichen Weisungen der Bundesagentur für Arbeit zu § 10 Abs. 2 Nr. 5 SGB II geprüft.</p>

  <section class="score-block">
    <div>
      <div class="score-num">${esc(get('SCORE_VALUE'))}<small>/100</small></div>
    </div>
    <div>
      <span class="verdict-pill">${esc(get('VERDICT'))}</span>
    </div>
    <div class="confidence">
      <strong>Konfidenz: ${esc(get('SCORE_CONFIDENCE'))} %</strong>
      <span class="conf-explain">${esc(get('CONFIDENCE_INTERPRETATION'))}</span>
    </div>
  </section>

  <h2>1. Mandanten- und Verfahrensdaten</h2>
  <table>
    <tr><th style="width: 38%">Mandant:in</th><td>${esc(get('CLIENT_FULL_NAME'))}${get('CLIENT_BIRTHDATE') ? `, geb. ${esc(get('CLIENT_BIRTHDATE'))}` : ''}</td></tr>
    ${get('CLIENT_ADDRESS') ? `<tr><th>Adresse</th><td>${esc(get('CLIENT_ADDRESS'))}</td></tr>` : ''}
    <tr><th>Aktenzeichen</th><td>${esc(get('CASE_ID'))}</td></tr>
    <tr><th>Leistungsbezug seit</th><td>${orNr('BENEFIT_START_DATE')}</td></tr>
    <tr><th>Datenübergabe</th><td>${orNr('DATA_SUBMISSION_DATE')}</td></tr>
    <tr><th>Beratungsgespräch</th><td>${renderMeetingLine(get)}</td></tr>
    <tr><th>Geprüfte Unterlagen</th><td>${renderDocumentList(get)}</td></tr>
  </table>

  <h2>2. Geschäftsdaten</h2>
  <table>
    <tr><th style="width: 38%">Branche</th><td>${orNr('BUSINESS_BRANCH')}</td></tr>
    <tr><th>Rechtsform</th><td>${orNr('BUSINESS_LEGAL_FORM')}</td></tr>
    <tr><th>Tätig seit</th><td>${orNr('BUSINESS_START_DATE')}</td></tr>
    <tr><th>Steuernummer</th><td>${orNr('BUSINESS_TAX_ID')}</td></tr>
    <tr><th>Tätigkeitsbeschreibung</th><td>${orNr('BUSINESS_DESCRIPTION')}</td></tr>
  </table>

  <h2>3. Wirtschaftliche Kennzahlen (monatlicher Durchschnitt)</h2>
  <table>
    <tr><th style="width: 38%">Umsatz monatlich</th><td>${esc(get('REVENUE_MONTHLY'))}${miniNote(get('REVENUE_ASSESSMENT'))}</td></tr>
    <tr><th>Kosten monatlich</th><td>${esc(get('COSTS_MONTHLY'))}${miniNote(get('COSTS_ASSESSMENT'))}</td></tr>
    <tr><th>Gewinn monatlich</th><td>${esc(get('PROFIT_MONTHLY'))}${miniNote(get('PROFIT_ASSESSMENT'))}</td></tr>
    <tr><th>Stundensatz (netto)</th><td>${esc(get('HOURLY_RATE_NET'))}${miniNote(get('HOURLY_RATE_ASSESSMENT'))}</td></tr>
    <tr><th>Liquide Mittel</th><td>${esc(get('LIQUIDITY'))}${miniNote(get('LIQUIDITY_ASSESSMENT'))}</td></tr>
    <tr><th>Liquiditäts-Reichweite</th><td>${esc(get('RUNWAY_WEEKS'))}${miniNote(get('RUNWAY_ASSESSMENT'))}</td></tr>
    <tr><th>Auslastung</th><td>${esc(get('UTILIZATION_PCT'))}${miniNote(get('UTILIZATION_ASSESSMENT'))}</td></tr>
  </table>

  <h2>4. Bedarfsgemeinschaft und Tragfähigkeitslücke</h2>
  <table>
    <tr><th style="width: 38%">Zusammensetzung</th><td>${esc(get('BG_DESCRIPTION'))}</td></tr>
    <tr><th>Regelbedarf gesamt</th><td>${esc(get('BG_REGELBEDARF'))}</td></tr>
    <tr><th>Kosten der Unterkunft</th><td>${esc(get('BG_KDU'))}</td></tr>
    <tr><th>Sonstiges Einkommen</th><td>${esc(get('BG_OTHER_INCOME'))}</td></tr>
    <tr><th>Gesamtbedarf</th><td><strong>${esc(get('BG_TOTAL_NEED'))}</strong></td></tr>
    <tr><th>Ungedeckter Bedarf</th><td><strong>${esc(get('BG_UNCOVERED'))}</strong></td></tr>
    <tr><th>Tragfähigkeitslücke</th><td><strong>${esc(get('TRAGFAEHIGKEITSLUECKE'))}</strong></td></tr>
  </table>
  <p>Die geprüfte Person ${esc(get('GAP_INTERPRETATION'))}, den Bedarf der Bedarfsgemeinschaft aus der selbständigen Tätigkeit zu decken.</p>

  <h2 class="section-break">5. Bewertung der sechs Tragfähigkeits-Dimensionen</h2>
  ${dimensions}

  <h2>6. Risikoindikatoren</h2>
  ${listOrParagraphs(get('RISK_INDICATORS'))}

  <h2>7. Stärken und Stabilisatoren</h2>
  ${listOrParagraphs(get('STRENGTH_INDICATORS'))}

  <h2>8. Erörterung der Zumutbarkeit (§ 10 Abs. 1 SGB II)</h2>
  ${paragraphs(get('CONSTRAINT_DISCUSSION'))}

  <section class="verdict-block">
    <h2 style="margin-top:0; border:none;">9. Ergebnis und Empfehlung</h2>
    ${paragraphs(get('VERDICT_LONG_PARAGRAPH'))}
    <h3>Begründung</h3>
    ${paragraphs(get('VERDICT_REASONING'))}
    <h3>Konkrete Empfehlung</h3>
    ${listOrParagraphs(get('RECOMMENDATION_DETAILED'))}
    ${conditions ? `<h3>Auflagen</h3>${paragraphs(conditions)}` : ''}
    ${reevaluation ? `<h3>Vorschlag zur Re-Evaluation</h3>${paragraphs(reevaluation)}` : ''}
  </section>

  <section class="signature">
    <div>
      <span class="label-mini">Mit freundlichen Grüßen</span>
      <p class="signature-name">${esc(get('COACH_NAME'))}</p>
      <p style="font-size:9pt; color: var(--color-stone-500); margin: 0;">${esc(get('PROVIDER_NAME'))}</p>
    </div>
    <div>
      <span class="label-mini">Datum</span>
      <p>${esc(get('STATEMENT_DATE'))}</p>
    </div>
  </section>

  <p class="footer">
    Diese Stellungnahme wurde nach den methodischen Vorgaben der Fachlichen Weisungen der Bundesagentur für Arbeit zu § 10 Abs. 2 Nr. 5 SGB II erstellt.
    Sie ist Entscheidungsvorbereitung im Sinne von Art. 22 DSGVO und ersetzt keine eigene Bewertung des Jobcenters.
  </p>
</body>
</html>`;
}
